import * as React from 'react';
import {
  type GeneratedLine,
  type GeneratedText,
  type GeneratedToken,
  generate,
  parse,
} from 'sugar-high/core';
import { tokenize } from 'sugar-high/lang/javascript';

type SafeHighlightNode =
  | {
      type: 'text';
      value: string;
    }
  | {
      type: 'span';
      className?: string;
      style?: React.CSSProperties;
      highlighted?: boolean;
      children: SafeHighlightNode[];
    };

const SAFE_CLASS_NAME_PATTERN = /^[a-zA-Z0-9:_\-\s]+$/;
const SAFE_SUGAR_HIGH_COLOR_PATTERN = /^var\(--sh-[a-z-]+\)$/;

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index++) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36);
}

function decodeHtmlEntities(value: string): string {
  return value.replace(
    /&(#x[\da-f]+|#\d+|amp|lt|gt|quot|apos|#39);/gi,
    (entity, code: string) => {
      switch (code.toLowerCase()) {
        case 'amp':
          return '&';
        case 'lt':
          return '<';
        case 'gt':
          return '>';
        case 'quot':
          return '"';
        case 'apos':
        case '#39':
          return "'";
        default:
          if (code.startsWith('#x')) {
            return String.fromCodePoint(Number.parseInt(code.slice(2), 16));
          }
          if (code.startsWith('#')) {
            return String.fromCodePoint(Number.parseInt(code.slice(1), 10));
          }
          return entity;
      }
    },
  );
}

function normalizeClassName(value: unknown): string | undefined {
  const className = Array.isArray(value) ? value.join(' ') : value;
  if (typeof className !== 'string' || className.length === 0) {
    return undefined;
  }
  return SAFE_CLASS_NAME_PATTERN.test(className) ? className : undefined;
}

function normalizeStyle(value: unknown): React.CSSProperties | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const color = (value as Record<string, unknown>).color;
  if (typeof color !== 'string' || !SAFE_SUGAR_HIGH_COLOR_PATTERN.test(color)) {
    return undefined;
  }

  return { color };
}

function textContent(node: SafeHighlightNode): string {
  if (node.type === 'text') {
    return node.value;
  }
  return node.children.map(textContent).join('');
}

function getNodeKey(
  node: SafeHighlightNode,
  seen: Map<string, number>,
): string {
  const base =
    node.type === 'text'
      ? `text-${hashString(node.value)}`
      : `span-${node.className ?? 'plain'}-${hashString(textContent(node))}`;
  const occurrence = seen.get(base) ?? 0;
  seen.set(base, occurrence + 1);
  return `${base}-${occurrence}`;
}

function HighlightNodes({ nodes }: { nodes: SafeHighlightNode[] }) {
  const seen = new Map<string, number>();

  return nodes.map((node) => {
    const key = getNodeKey(node, seen);

    if (node.type === 'text') {
      return <React.Fragment key={key}>{node.value}</React.Fragment>;
    }

    return (
      <span
        key={key}
        className={node.className}
        style={node.style}
        data-highlighted={node.highlighted || undefined}
      >
        <HighlightNodes nodes={node.children} />
      </span>
    );
  });
}

function renderNodes(nodes: SafeHighlightNode[]): React.ReactNode {
  return <HighlightNodes nodes={nodes} />;
}

function toSafeHighlightNode(
  node: GeneratedLine | GeneratedToken | GeneratedText,
): SafeHighlightNode | null {
  if (node.type === 'text') {
    return { type: 'text', value: node.value };
  }

  if (node.tagName !== 'span') {
    return null;
  }

  return {
    type: 'span',
    className: normalizeClassName(node.properties?.className),
    style: normalizeStyle(node.properties?.style),
    children: node.children
      .map((child) => toSafeHighlightNode(child))
      .filter((child): child is SafeHighlightNode => child !== null),
  };
}

function parseStyleAttribute(value: string): React.CSSProperties | undefined {
  const declarations: string[] = [];
  for (const declaration of value.split(';')) {
    const trimmedDeclaration = declaration.trim();
    if (trimmedDeclaration) {
      declarations.push(trimmedDeclaration);
    }
  }

  if (declarations.length !== 1) {
    return undefined;
  }

  const [property, rawColor] = declarations[0]
    .split(':')
    .map((part) => part.trim());
  if (
    property !== 'color' ||
    !rawColor ||
    !SAFE_SUGAR_HIGH_COLOR_PATTERN.test(rawColor)
  ) {
    return undefined;
  }

  return { color: rawColor };
}

function parseSpanAttributes(
  tag: string,
): Pick<
  Extract<SafeHighlightNode, { type: 'span' }>,
  'className' | 'style'
> | null {
  const classNameMatch = tag.match(/\sclass=(["'])(.*?)\1/i);
  const styleMatch = tag.match(/\sstyle=(["'])(.*?)\1/i);
  const disallowedAttributeMatch = tag.match(
    /\s(?!class=|style=)[a-zA-Z][a-zA-Z0-9:-]*=/,
  );

  if (disallowedAttributeMatch) {
    return null;
  }

  const className = classNameMatch
    ? normalizeClassName(decodeHtmlEntities(classNameMatch[2]))
    : undefined;
  const style = styleMatch
    ? parseStyleAttribute(decodeHtmlEntities(styleMatch[2]))
    : undefined;

  if ((classNameMatch && !className) || (styleMatch && !style)) {
    return null;
  }

  return { className, style };
}

function parseHighlightedSpanMarkup(
  markup: string,
): SafeHighlightNode[] | null {
  const root: Extract<SafeHighlightNode, { type: 'span' }> = {
    type: 'span',
    children: [],
  };
  const stack = [root];
  const tagPattern = /<\/?span(?:\s+[^>]*)?>/gi;
  let cursor = 0;
  let match: RegExpExecArray | null;

  match = tagPattern.exec(markup);
  while (match !== null) {
    const textBeforeTag = markup.slice(cursor, match.index);
    if (textBeforeTag) {
      stack.at(-1)?.children.push({
        type: 'text',
        value: decodeHtmlEntities(textBeforeTag),
      });
    }

    const tag = match[0];
    if (tag.startsWith('</')) {
      if (stack.length === 1) {
        return null;
      }
      stack.pop();
    } else {
      const attributes = parseSpanAttributes(tag);
      if (!attributes) {
        return null;
      }
      const node: SafeHighlightNode = {
        type: 'span',
        ...attributes,
        children: [],
      };
      stack.at(-1)?.children.push(node);
      stack.push(node);
    }

    cursor = match.index + tag.length;
    match = tagPattern.exec(markup);
  }

  const remainingText = markup.slice(cursor);
  if (remainingText) {
    stack.at(-1)?.children.push({
      type: 'text',
      value: decodeHtmlEntities(remainingText),
    });
  }

  return stack.length === 1 ? root.children : null;
}

export function renderHighlightedCode(
  code: string,
  highlightedLines: readonly number[] = [],
): React.ReactNode {
  const lines = generate(
    parse(code, { tokenize: (source) => tokenize(source, undefined) }),
  );
  return renderNodes(
    lines
      .map((line) => toSafeHighlightNode(line))
      .filter((line): line is SafeHighlightNode => line !== null)
      .map((line, index) => ({
        ...line,
        highlighted: highlightedLines.includes(index + 1),
      })),
  );
}

export function renderHighlightedMarkup(markup: string): React.ReactNode {
  const parsedNodes = parseHighlightedSpanMarkup(markup);
  return parsedNodes ? renderNodes(parsedNodes) : markup;
}
